import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../api";
import { Card, EmptyState, formatDate } from "../../ui";
import { DateInput } from "../../DateInput";

export function AtasTab({ isAdmin }) {
  const [atas, setAtas] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", meeting_date: "", summary: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(
    () =>
      api.get("/portal/atas").then((r) => setAtas(r.data)).catch((e) => { setAtas([]); console.warn("Falha ao carregar atas:", e); }),
    [],
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("meeting_date", form.meeting_date);
    fd.append("summary", form.summary);
    if (file) fd.append("pdf", file);
    try {
      await api.post("/admin/atas", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ata registrada!");
      setOpen(false);
      setForm({ title: "", meeting_date: "", summary: "" });
      setFile(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta ata?")) return;
    try {
      await api.delete(`/admin/atas/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const download = async (a) => {
    try {
      const res = await api.get(`/portal/atas/${a.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = a.pdf_name || `${a.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("PDF indisponível.");
    }
  };

  return (
    <div>
      {isAdmin && (
        <div className="mb-4">
          <button
            data-testid="new-ata-btn"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Nova ata
          </button>
        </div>
      )}
      {open && isAdmin && (
        <Card className="p-5 mb-5">
          <form data-testid="new-ata-form" onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Título *
              </label>
              <input
                data-testid="ata-title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Data da reunião *
              </label>
              <DateInput
                testid="ata-date"
                required
                value={form.meeting_date}
                onChange={(v) => setForm({ ...form, meeting_date: v })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Resumo
              </label>
              <textarea
                data-testid="ata-summary"
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[120px]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                PDF (opcional)
              </label>
              <input
                data-testid="ata-pdf"
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <button
                data-testid="ata-submit"
                className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
              >
                Salvar ata
              </button>
            </div>
          </form>
        </Card>
      )}

      {atas === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : atas.length === 0 ? (
        <EmptyState
          title="Sem atas registradas"
          hint={isAdmin ? "Crie a primeira ata acima." : "Aguarde o administrador publicar."}
        />
      ) : (
        <ul className="space-y-3">
          {atas.map((a) => (
            <li
              key={a.id}
              data-testid={`ata-row-${a.id}`}
              className="bg-white border border-slate-200 p-5 rounded-md flex flex-col md:flex-row md:items-start gap-4"
            >
              <div className="h-12 w-12 grid place-items-center bg-slate-100 text-slate-600 rounded-sm shrink-0">
                <FileText size={20} strokeWidth={1.4} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500">
                  {formatDate(a.meeting_date)}
                </div>
                <h3 className="mt-1 font-serif-display text-xl">{a.title}</h3>
                {a.summary && (
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-line line-clamp-3">
                    {a.summary}
                  </p>
                )}
              </div>
              <div className="flex md:flex-col gap-2">
                {a.pdf_name && (
                  <button
                    data-testid={`download-ata-${a.id}`}
                    onClick={() => download(a)}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
                  >
                    <Download size={14} /> PDF
                  </button>
                )}
                {isAdmin && (
                  <button
                    data-testid={`delete-ata-${a.id}`}
                    onClick={() => remove(a.id)}
                    className="px-3 py-2 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-50"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
